import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Globe, Edit, CheckCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSites, useLead } from '@/hooks/useFirestore';
import { cloudFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { OutreachModal } from '@/components/site/OutreachModal';
import { markSiteSent } from '@/lib/outreach';
import type { Site } from '@shared/types';

function getStatusStyle(status: string) {
  switch (status) {
    case 'sent':
      return { background: 'rgba(91, 58, 58, 0.2)', color: '#B08080' };
    case 'sold':
      return { background: 'rgba(136, 136, 136, 0.2)', color: '#888888' };
    case 'draft':
    case 'preview':
    default:
      return { background: 'rgba(255,255,255,0.1)', color: '#888888' };
  }
}

export function SitesPage() {
  const { data: sites, isLoading, error } = useSites();
  const { userData } = useAuth();
  const [outreachModalSite, setOutreachModalSite] = useState<(Site & { id: string }) | null>(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);

  const hasStripeConnect = userData?.stripeConnectStatus === 'active';
  const hasConnectAccount = !!userData?.stripeConnectAccountId;

  // Opportunistic sync: if the Stripe account.updated webhook missed an event,
  // reconcile status whenever the user lands on this page with a Connect
  // account that isn't yet marked active. Prevents the "Set Up Payouts First"
  // button from sticking after the user has actually finished onboarding.
  useEffect(() => {
    if (!hasConnectAccount || hasStripeConnect) return;
    cloudFunctions.getConnectStatus({}).catch(() => { /* best-effort */ });
  }, [hasConnectAccount, hasStripeConnect]);

  // Fetch lead when outreach modal is open
  const { data: outreachLead, isLoading: isLoadingLead, error: leadError } = useLead(outreachModalSite?.leadId);

  useEffect(() => {
    if (leadError) {
      toast.error('Failed to load lead details');
    }
  }, [leadError]);

  // Handle closing modal and optionally marking site as sent
  const handleCloseOutreachModal = async (markAsSent?: boolean) => {
    if (markAsSent && outreachModalSite) {
      try {
        const { logAnalyticsEvent } = await import('@/lib/firebase');
        await markSiteSent(outreachModalSite.id, outreachModalSite.leadId);
        logAnalyticsEvent('outreach_sent', { site_id: outreachModalSite.id });
        toast.success('Site marked as sent!');
      } catch (err) {
        console.error('Failed to update site status:', err);
        toast.error('Failed to mark site as sent');
      }
    }
    setOutreachModalSite(null);
  };

  const handleOpenPreview = async (siteId: string, existingToken?: string) => {
    if (existingToken) {
      window.open(`/p/${existingToken}`, '_blank');
      return;
    }

    setLoadingPreviewId(siteId);
    try {
      const { token } = await cloudFunctions.getPreviewShareLink({ siteId });
      window.open(`/p/${token}`, '_blank');
    } catch (err) {
      toast.error('Failed to open preview');
    } finally {
      setLoadingPreviewId(null);
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Sites</h1>
          <p className="text-gray-400 mt-1">Error loading sites</p>
        </div>
        <div style={{ background: 'rgba(255,0,0,0.2)', padding: '20px', borderRadius: '8px', color: '#FFFBFE' }}>
          Error: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Sites</h1>
          <p className="text-gray-400 mt-1">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Sites</h1>
        <p className="text-gray-400 mt-1">{sites?.length || 0} total sites</p>
      </div>

      {sites && sites.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {sites.map((site) => {
            const statusStyle = getStatusStyle(site.status);

            return (
              <div
                key={site.id}
                className="hover-lift animate-list-in"
                style={{
                  background: 'rgba(15, 15, 15, 0.95)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                }}
              >
                {site.businessData?.photos?.[0] ? (
                  <div style={{
                    aspectRatio: '16/9',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    overflow: 'hidden',
                  }}>
                    <img
                      src={site.businessData.photos[0]}
                      alt={site.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.style.background = 'linear-gradient(135deg, rgba(91, 58, 58, 0.1), rgba(91, 58, 58, 0.05))';
                        e.currentTarget.parentElement!.style.display = 'flex';
                        e.currentTarget.parentElement!.style.alignItems = 'center';
                        e.currentTarget.parentElement!.style.justifyContent = 'center';
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    aspectRatio: '16/9',
                    background: 'linear-gradient(135deg, rgba(91, 58, 58, 0.1), rgba(91, 58, 58, 0.05))',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Globe style={{ width: '48px', height: '48px', color: 'rgba(91, 58, 58, 0.5)' }} />
                  </div>
                )}
                <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>{site.name}</h3>
                <p style={{ color: '#888888', fontSize: '13px', marginBottom: '8px' }}>{site.templateType}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span
                    style={{
                      background: statusStyle.background,
                      color: statusStyle.color,
                      padding: '4px 12px',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {site.status === 'sold' && <CheckCircle style={{ width: '12px', height: '12px' }} />}
                    {site.status}
                  </span>
                </div>

                {/* Primary Actions - Only show for draft/preview sites */}
                {(site.status === 'draft' || site.status === 'preview') && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <button
                      onClick={() => handleOpenPreview(site.id, site.previewToken)}
                      disabled={loadingPreviewId === site.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: 'rgba(255,255,255,0.1)',
                        color: '#FFFBFE',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: loadingPreviewId === site.id ? 'wait' : 'pointer',
                        fontSize: '14px',
                        opacity: loadingPreviewId === site.id ? 0.7 : 1,
                      }}
                    >
                      {loadingPreviewId === site.id ? (
                        <>
                          <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                          Loading...
                        </>
                      ) : (
                        'Preview'
                      )}
                    </button>
                    <Link
                      to={`/dashboard/sites/${site.id}/edit`}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: 'linear-gradient(to right, #B08080, #B08080)',
                        color: '#FFFBFE',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '14px',
                      }}
                    >
                      <Edit style={{ width: '16px', height: '16px' }} />
                      Edit
                    </Link>
                  </div>
                )}

                {/* Secondary Actions - Send to Business (adaptive for email or SMS) */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(site.status === 'draft' || site.status === 'preview') && (
                    <button
                      onClick={() => {
                        if (!hasStripeConnect) {
                          toast.error('Set up Stripe Connect in Settings → Payouts before sending to businesses');
                          return;
                        }
                        setOutreachModalSite(site);
                      }}
                      title={!hasStripeConnect ? 'Set up Stripe Connect first' : 'Send preview to business'}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: hasStripeConnect ? 'rgba(91, 58, 58, 0.15)' : 'rgba(255,255,255,0.05)',
                        color: hasStripeConnect ? '#B08080' : '#666',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: `1px solid ${hasStripeConnect ? 'rgba(91, 58, 58, 0.2)' : 'rgba(255,255,255,0.1)'}`,
                        cursor: 'pointer',
                        fontSize: '13px',
                        opacity: hasStripeConnect ? 1 : 0.7,
                      }}
                    >
                      <Send style={{ width: '14px', height: '14px' }} />
                      {hasStripeConnect ? 'Send to Business' : 'Set Up Payouts First'}
                    </button>
                  )}
                  {site.status === 'sent' && (
                    <>
                      <Link
                        to={`/dashboard/sites/${site.id}/edit`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          background: 'rgba(255,255,255,0.1)',
                          color: '#FFFBFE',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '13px',
                        }}
                      >
                        <Edit style={{ width: '14px', height: '14px' }} />
                        Edit
                      </Link>
                      <button
                        onClick={() => {
                          if (!hasStripeConnect) {
                            toast.error('Set up Stripe Connect in Settings → Payouts before sending to businesses');
                            return;
                          }
                          setOutreachModalSite(site);
                        }}
                        title="Send updated preview to business"
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          background: 'rgba(91, 58, 58, 0.15)',
                          color: '#B08080',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(91, 58, 58, 0.2)',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        <Send style={{ width: '14px', height: '14px' }} />
                        Send Updated Site
                      </button>
                    </>
                  )}
                  {site.status === 'sold' && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: 'rgba(34, 197, 94, 0.15)',
                        color: '#22c55e',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        border: '1px solid rgba(34, 197, 94, 0.2)',
                      }}
                    >
                      <CheckCircle style={{ width: '14px', height: '14px' }} />
                      Sold
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
          }}
        >
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(91, 58, 58, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Globe style={{ width: '32px', height: '32px', color: '#B08080' }} />
          </div>
          <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>0 Sites</h3>
          <p style={{ color: '#888888', marginBottom: '16px' }}>
            First scrape leads, then click "Generate Site" on a lead
          </p>
          <Link
            to="/dashboard/leads"
            style={{
              background: 'linear-gradient(to right, #B08080, #B08080)',
              color: '#FFFBFE',
              padding: '10px 20px',
              borderRadius: '8px',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            View Leads
          </Link>
        </div>
      )}

      {/* Outreach Modal (Email or SMS) - portaled to body to avoid transform containing block */}
      {outreachModalSite && createPortal(
        <OutreachModal
          site={outreachModalSite}
          lead={outreachLead || null}
          isLoadingLead={isLoadingLead}
          onClose={handleCloseOutreachModal}
          readOnly={outreachModalSite.status === 'sold'}
        />,
        document.body
      )}
    </div>
  );
}
